// Real per-inverter panel for the station card (R6): live cards + 3-phase table
// + per-inverter day history (power, voltage, current). Data via /scada/inverters.
import { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { SectionCard } from '@/components/scada/kit';
import { scada, stateColor, type OpState } from '@/scada/tokens';
import { useInverterLatest, useInverterDay } from '@/scada/useInverters';

const f1 = (v: number | null | undefined) => (v == null ? '—' : Number(v).toFixed(1));
const dot = (s: OpState) => <span style={{ width: 8, height: 8, borderRadius: 999, background: stateColor(s), display: 'inline-block' }} />;

export default function InverterPanel({ assetId }: { assetId?: string }) {
  const { data, meta, err } = useInverterLatest(assetId);
  const { series } = useInverterDay(assetId);
  const [sel, setSel] = useState<number | null>(null);

  const selLatest = data?.find((d) => d.unitId === sel) ?? null;
  const selSeries = useMemo(() => series?.find((s) => s.unitId === sel)?.points ?? [], [series, sel]);

  if (err || meta?.mode === 'none') {
    return <SectionCard title="Інвертори"><span style={{ color: scada.textMuted, fontSize: 13 }}>Дані по інверторах недоступні для цієї станції.</span></SectionCard>;
  }
  if (!data || !meta) return <SectionCard title="Інвертори"><span style={{ color: scada.textMuted, fontSize: 13 }}>Завантаження…</span></SectionCard>;

  // Aggregate stations (SMA): draw the whole fleet as a synthesized grid grouped
  // under the SMA Data Manager, with values estimated from the plant total.
  if (meta.mode === 'aggregate') {
    return (
      <SectionCard title="Інвертори" subtitle="Візуалізація парку · орієнтовні значення з сумарного показника">
        <div className="flex items-center gap-2 flex-wrap" style={{ background: `${scada.state.curtailed}12`, border: `1px solid ${scada.state.curtailed}33`, borderRadius: 8, padding: '9px 13px', marginBottom: 12 }}>
          <i className="ri-server-line" style={{ color: scada.state.curtailed }} />
          <span style={{ color: scada.text, fontSize: 13, fontWeight: 600 }}>{meta.groupName ?? 'SMA Data Manager'}</span>
          <span style={{ color: scada.textMuted, fontSize: 12 }}>· {meta.availableInverters ?? '—'}/{meta.expectedInverters ?? '—'} у роботі · сумарно {meta.plantPacKw != null ? (meta.plantPacKw / 1000).toFixed(2) : '—'} МВт</span>
          <span style={{ color: scada.textMuted, fontSize: 11, marginLeft: 'auto', flex: 1, textAlign: 'right', minWidth: 200 }}>
            Контролер передає лише сумарні дані — значення по інверторах орієнтовні (рівномірний розподіл)
          </span>
        </div>
        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))' }}>
          {data.map((inv) => {
            const off = inv.status === 'offline';
            const c = off ? scada.state.offline : scada.state.ok;
            return (
              <div key={inv.unitId} style={{ textAlign: 'center', background: `${c}1A`, border: `1px solid ${c}55`, borderTop: `3px solid ${c}`, borderRadius: 8, padding: '8px 6px' }}>
                <div style={{ color: scada.text, fontSize: 12, fontWeight: 600 }}>INV-{String(inv.unitId).padStart(2, '0')}</div>
                <div style={{ color: c, fontSize: 16, fontWeight: 700, lineHeight: 1.1, marginTop: 2 }}>{off ? '—' : `${inv.percentOfNominal ?? '—'}%`}</div>
                <div style={{ color: scada.textDim, fontSize: 11 }}>{off ? 'офлайн' : `${f1(inv.pacKw)} кВт`}</div>
                <div style={{ color: scada.textMuted, fontSize: 9, marginTop: 2 }}>{off ? '' : 'оцінка'}</div>
              </div>
            );
          })}
        </div>
      </SectionCard>
    );
  }

  const working = data.filter((d) => d.status === 'ok').length;

  // ── Comparison: median of the group + deviation per inverter ──────────────
  const pcts = data.map((d) => d.percentOfNominal).filter((x): x is number => x != null);
  const median = pcts.length ? [...pcts].sort((a, b) => a - b)[Math.floor(pcts.length / 2)] : null;
  const devOf = (inv: typeof data[number]) =>
    median != null && inv.percentOfNominal != null ? inv.percentOfNominal - median : null;
  const heatColor = (inv: typeof data[number]) => {
    if (inv.status === 'offline') return scada.state.offline;
    const d = devOf(inv);
    if (d == null) return scada.textMuted;
    if (d >= -3) return scada.state.ok;
    if (d >= -10) return scada.state.warning;
    return scada.state.alarm;
  };
  const laggards = data.filter((inv) => { const d = devOf(inv); return d != null && d < -10; });

  return (
    <>
      <SectionCard
        title={`Інвертори (${working}/${data.length} у роботі)`}
        subtitle="Теплова мапа за відхиленням від медіани групи · натисніть інвертор для деталей"
        right={median != null ? <span style={{ color: scada.textMuted, fontSize: 11 }}>медіана {median}%</span> : undefined}
      >
        {/* Comparison banner */}
        {laggards.length > 0 ? (
          <div style={{ background: `${scada.state.alarm}14`, border: `1px solid ${scada.state.alarm}40`, color: scada.state.alarm, borderRadius: 8, padding: '7px 12px', fontSize: 12, marginBottom: 12 }}>
            <i className="ri-alert-line" style={{ marginRight: 6 }} />
            Відстають від групи: {laggards.map((l) => `INV-${String(l.unitId).padStart(2, '0')} (${devOf(l)!.toFixed(0)}%)`).join(', ')}
          </div>
        ) : (
          <div style={{ color: scada.textMuted, fontSize: 12, marginBottom: 12 }}>
            <i className="ri-check-line" style={{ marginRight: 6, color: scada.state.ok }} />Усі інвертори в межах норми відносно групи.
          </div>
        )}

        {/* Heatmap grid */}
        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))' }}>
          {data.map((inv) => {
            const c = heatColor(inv);
            const dev = devOf(inv);
            return (
              <button key={inv.unitId} onClick={() => setSel(inv.unitId)}
                style={{ textAlign: 'center', background: `${c}1A`, border: `1px solid ${inv.unitId === sel ? scada.accent : c + '55'}`, borderTop: `3px solid ${c}`, borderRadius: 8, padding: '8px 6px', cursor: 'pointer' }}>
                <div style={{ color: scada.text, fontSize: 12, fontWeight: 600 }}>INV-{String(inv.unitId).padStart(2, '0')}</div>
                <div style={{ color: c, fontSize: 18, fontWeight: 700, lineHeight: 1.1, marginTop: 2 }}>{inv.percentOfNominal != null ? `${inv.percentOfNominal}%` : '—'}</div>
                <div style={{ color: scada.textDim, fontSize: 11 }}>{f1(inv.pacKw)} кВт</div>
                <div style={{ color: dev == null ? scada.textMuted : dev >= -3 ? scada.textMuted : c, fontSize: 10, marginTop: 2 }}>
                  {dev == null ? (inv.status === 'offline' ? 'офлайн' : '—') : `${dev > 0 ? '+' : ''}${dev.toFixed(0)}% до медіани`}
                </div>
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-3 flex-wrap" style={{ marginTop: 10 }}>
          {[
            { c: scada.state.ok, t: 'в нормі (≥ −3%)' },
            { c: scada.state.warning, t: 'нижче (−3…−10%)' },
            { c: scada.state.alarm, t: 'відстає (< −10%)' },
            { c: scada.state.offline, t: 'офлайн' },
          ].map((l) => (
            <span key={l.t} className="inline-flex items-center gap-1.5" style={{ color: scada.textMuted, fontSize: 10 }}>
              <span style={{ width: 9, height: 9, borderRadius: 2, background: l.c, display: 'inline-block' }} />{l.t}
            </span>
          ))}
        </div>
      </SectionCard>

      {selLatest && (
        <div style={{ marginTop: 16 }}>
          <SectionCard
            title={`INV-${String(selLatest.unitId).padStart(2, '0')} — деталі`}
            subtitle={`оновлено ${new Date(selLatest.readAtLocal).toLocaleTimeString('uk-UA')}`}
            right={<button onClick={() => setSel(null)} style={{ background: 'transparent', border: 'none', color: scada.textMuted, cursor: 'pointer', fontSize: 16 }}><i className="ri-close-line" /></button>}
          >
            {/* Live 3-phase table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', minWidth: 360 }}>
                <thead>
                  <tr style={{ background: scada.panel2 }}>
                    {['Фаза', 'Напруга, В', 'Струм, А', 'Потужність, кВт'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', color: scada.textMuted, fontSize: 11, fontWeight: 600, padding: '7px 14px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(['a', 'b', 'c'] as const).map((ph) => (
                    <tr key={ph} style={{ borderTop: `1px solid ${scada.border}` }}>
                      <td style={{ padding: '6px 14px', color: scada.text, fontSize: 13, fontWeight: 600 }}>{ph.toUpperCase()}</td>
                      <td style={{ padding: '6px 14px', color: scada.textDim, fontSize: 13 }}>{f1(selLatest.phases[ph].v)}</td>
                      <td style={{ padding: '6px 14px', color: scada.textDim, fontSize: 13 }}>{f1(selLatest.phases[ph].i)}</td>
                      <td style={{ padding: '6px 14px', color: scada.textDim, fontSize: 13 }}>{f1(selLatest.phases[ph].p)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: `1px solid ${scada.borderStrong}`, background: scada.panel2 }}>
                    <td style={{ padding: '6px 14px', color: scada.text, fontSize: 12, fontWeight: 700 }}>Σ / DC</td>
                    <td style={{ padding: '6px 14px', color: scada.textMuted, fontSize: 12 }}>{f1(selLatest.facHz)} Гц</td>
                    <td style={{ padding: '6px 14px', color: scada.textMuted, fontSize: 12 }}>DC {f1(selLatest.ppvKw)} кВт</td>
                    <td style={{ padding: '6px 14px', color: scada.text, fontSize: 12, fontWeight: 700 }}>AC {f1(selLatest.pacKw)} кВт</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* History: power */}
            {selSeries.length > 0 && (
              <>
                <div style={{ color: scada.textMuted, fontSize: 11, margin: '14px 0 4px' }}>Потужність за добу, кВт</div>
                <div style={{ width: '100%', height: 200 }}>
                  <ResponsiveContainer>
                    <LineChart data={selSeries} margin={{ top: 6, right: 8, bottom: 0, left: -12 }}>
                      <CartesianGrid stroke={scada.border} strokeDasharray="3 3" />
                      <XAxis dataKey="t" tick={{ fill: scada.textMuted, fontSize: 10 }} stroke={scada.border} minTickGap={28} />
                      <YAxis tick={{ fill: scada.textMuted, fontSize: 10 }} stroke={scada.border} />
                      <Tooltip contentStyle={{ background: scada.panel, border: `1px solid ${scada.border}`, borderRadius: 8, fontSize: 12 }} labelStyle={{ color: scada.text }} />
                      <Line type="monotone" dataKey="pac" name="AC" stroke={scada.state.ok} strokeWidth={2} dot={false} connectNulls />
                      <Line type="monotone" dataKey="ppv" name="DC" stroke={scada.state.curtailed} strokeWidth={1.5} dot={false} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div style={{ color: scada.textMuted, fontSize: 11, margin: '12px 0 4px' }}>Напруга фаз за добу, В</div>
                <div style={{ width: '100%', height: 180 }}>
                  <ResponsiveContainer>
                    <LineChart data={selSeries} margin={{ top: 6, right: 8, bottom: 0, left: -12 }}>
                      <CartesianGrid stroke={scada.border} strokeDasharray="3 3" />
                      <XAxis dataKey="t" tick={{ fill: scada.textMuted, fontSize: 10 }} stroke={scada.border} minTickGap={28} />
                      <YAxis domain={['auto', 'auto']} tick={{ fill: scada.textMuted, fontSize: 10 }} stroke={scada.border} />
                      <Tooltip contentStyle={{ background: scada.panel, border: `1px solid ${scada.border}`, borderRadius: 8, fontSize: 12 }} labelStyle={{ color: scada.text }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="va" name="Uₐ" stroke="#ef4444" strokeWidth={1.5} dot={false} connectNulls />
                      <Line type="monotone" dataKey="vb" name="U_b" stroke="#22c55e" strokeWidth={1.5} dot={false} connectNulls />
                      <Line type="monotone" dataKey="vc" name="U_c" stroke="#38bdf8" strokeWidth={1.5} dot={false} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div style={{ color: scada.textMuted, fontSize: 11, margin: '12px 0 4px' }}>Струм фаз за добу, А</div>
                <div style={{ width: '100%', height: 180 }}>
                  <ResponsiveContainer>
                    <LineChart data={selSeries} margin={{ top: 6, right: 8, bottom: 0, left: -12 }}>
                      <CartesianGrid stroke={scada.border} strokeDasharray="3 3" />
                      <XAxis dataKey="t" tick={{ fill: scada.textMuted, fontSize: 10 }} stroke={scada.border} minTickGap={28} />
                      <YAxis tick={{ fill: scada.textMuted, fontSize: 10 }} stroke={scada.border} />
                      <Tooltip contentStyle={{ background: scada.panel, border: `1px solid ${scada.border}`, borderRadius: 8, fontSize: 12 }} labelStyle={{ color: scada.text }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="ia" name="Iₐ" stroke="#ef4444" strokeWidth={1.5} dot={false} connectNulls />
                      <Line type="monotone" dataKey="ib" name="I_b" stroke="#22c55e" strokeWidth={1.5} dot={false} connectNulls />
                      <Line type="monotone" dataKey="ic" name="I_c" stroke="#38bdf8" strokeWidth={1.5} dot={false} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </SectionCard>
        </div>
      )}
    </>
  );
}
